// packages/shared-components/src/components/Palette/Palette.tsx

import { useState, useRef, useEffect, useCallback } from "react";
import { PaletteProps } from "./types";
import { hexToRgb, rgbToHex, rgbToHsv, hsvToRgb } from "../../utils/color";
import { getDefaultPaletteColors } from "./paletteUtils";
import "./Palette.unscoped.css";
import "./Palette.scss";
import { useTranslation } from "react-i18next";
import { useMessage } from "../../hooks/useMessage";

/**
 * 调色板组件
 *
 * 基于 HSV 颜色空间的交互式调色板，模拟真实画家调色板的使用体验
 *
 * @example
 * ```tsx
 * <Palette
 *   value={color}
 *   onChange={setColor}
 *   saturationWidth={240}
 *   saturationHeight={200}
 * />
 * ```
 */
export function Palette({
  value,
  defaultValue = "#239894",
  onChange,
  onChangeEnd,
  disabled = false,
  presetColors = getDefaultPaletteColors(),
  className = "",
  saturationWidth = "100%",
  saturationHeight = 194,
  hueWidth = "100%",
  hueHeight = 194,
  presetColumns = 2,
  showClearButton = false,
  onClearColorCustom,
}: PaletteProps) {
  const { t } = useTranslation();
  const { warning: showMessageWarning } = useMessage();
  const hexInvalidHintAt = useRef(0);

  const showHexInvalidCharacterMessage = useCallback(() => {
    const now = Date.now();
    if (now - hexInvalidHintAt.current < 800) {
      return;
    }
    hexInvalidHintAt.current = now;
    showMessageWarning(t("keyboard.lighting.hexInvalidCharacter"), 2500);
  }, [showMessageWarning, t]);

  if (className) {
    console.log(className);
  }
  // ========== 调色板状态 ==========
  const [internalValue, setInternalValue] = useState(defaultValue);

  // 判断受控模式：当 value 不为 undefined 且不为 null 时视为受控
  const isControlled = value !== undefined && value !== null;
  const currentColor = isControlled ? value : internalValue;

  const rgb = hexToRgb(currentColor || "#000000");
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

  const [hue, setHue] = useState(hsv.h); // 色相 (0-360°)
  const [saturation, setSaturation] = useState(hsv.s); // 饱和度 (0-100%)
  const [brightness, setBrightness] = useState(hsv.v); // 明度 (0-100%)

  const [isDraggingSaturation, setIsDraggingSaturation] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);

  const saturationRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  // ========== 同步外部颜色（只在非拖拽状态） ==========
  useEffect(() => {
    if (isDraggingSaturation || isDraggingHue) {
      return;
    }

    const rgb = hexToRgb(currentColor || "#000000");
    const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

    // 避免在黑色/灰色时重置色相
    if (newHsv.v > 5 && newHsv.s > 5) {
      setHue(newHsv.h);
    }

    setSaturation(newHsv.s);
    setBrightness(newHsv.v);
  }, [currentColor, isDraggingSaturation, isDraggingHue]);

  // ========== 更新颜色 ==========
  const updateColor = (h: number, s: number, v: number) => {
    const rgb = hsvToRgb(h, s, v);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);

    if (!isControlled) {
      setInternalValue(hex);
    }
    onChange?.(hex);
  };

  // ========== 饱和度面板拖拽 ==========
  const handleSaturationMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDraggingSaturation(true);
    handleSaturationMove(e);
  };

  const handleSaturationMove = (e: React.MouseEvent | MouseEvent) => {
    if (!saturationRef.current) return;

    const rect = saturationRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

    const newSaturation = (x / rect.width) * 100;
    const newBrightness = 100 - (y / rect.height) * 100;

    setSaturation(newSaturation);
    setBrightness(newBrightness);
    updateColor(hue, newSaturation, newBrightness);
  };

  // ========== 色相条拖拽 ==========
  const handleHueMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDraggingHue(true);
    handleHueMove(e);
  };

  const handleHueMove = (e: React.MouseEvent | MouseEvent) => {
    if (!hueRef.current) return;

    const rect = hueRef.current.getBoundingClientRect();
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    const newHue = (y / rect.height) * 360;

    setHue(newHue);
    updateColor(newHue, saturation, brightness);
  };

  // ========== 全局鼠标事件 ==========
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingSaturation) {
        handleSaturationMove(e);
      } else if (isDraggingHue) {
        handleHueMove(e);
      }
    };

    const handleMouseUp = () => {
      const wasDrag = isDraggingSaturation || isDraggingHue;
      setIsDraggingSaturation(false);
      setIsDraggingHue(false);
      if (wasDrag) {
        const rgb = hsvToRgb(hue, saturation, brightness);
        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
        onChangeEnd?.(hex);
      }
    };

    if (isDraggingSaturation || isDraggingHue) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDraggingSaturation,
    isDraggingHue,
    hue,
    saturation,
    brightness,
    onChangeEnd,
  ]);

  // ========== RGB 输入处理 ==========
  const handleRgbChange = (channel: "r" | "g" | "b", value: string) => {
    const numValue = Math.max(0, Math.min(255, parseInt(value) || 0));
    const newRgb = { ...rgb, [channel]: numValue };
    const newHsv = rgbToHsv(newRgb.r, newRgb.g, newRgb.b);

    if (newHsv.v > 5 && newHsv.s > 5) {
      setHue(newHsv.h);
    }
    setSaturation(newHsv.s);
    setBrightness(newHsv.v);
    updateColor(newHsv.h, newHsv.s, newHsv.v);
  };

  // ========== HEX 输入处理 ==========
  const handleHexChange = (value: string) => {
    const withoutHash = value.replace(/^#/, "");
    if (/[^0-9A-Fa-f]/.test(withoutHash)) {
      showHexInvalidCharacterMessage();
    }
    let hex = withoutHash.replace(/[^0-9A-Fa-f]/g, "");
    if (hex.length > 6) hex = hex.slice(0, 6);
    setHexDraft(hex);

    if (hex.length === 6) {
      const fullHex = `#${hex}`;
      const rgb = hexToRgb(fullHex);
      const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

      if (newHsv.v > 5 && newHsv.s > 5) {
        setHue(newHsv.h);
      }
      setSaturation(newHsv.s);
      setBrightness(newHsv.v);

      if (!isControlled) {
        setInternalValue(fullHex);
      }
      onChange?.(fullHex);
      onChangeEnd?.(fullHex);
    }
  };

  // ========== 预设颜色点击 ==========
  const handlePresetClick = (color: string) => {
    if (disabled) return;
    const rgb = hexToRgb(color);
    const newHsv = rgbToHsv(rgb.r, rgb.g, rgb.b);

    setHue(newHsv.h);
    setSaturation(newHsv.s);
    setBrightness(newHsv.v);

    if (!isControlled) {
      setInternalValue(color);
    }
    onChange?.(color);
    onChangeEnd?.(color);
  };

  // ========== 计算饱和度面板背景色 ==========
  const saturationBgColor = hsvToRgb(hue, 100, 100);
  const saturationBg = `rgb(${saturationBgColor.r}, ${saturationBgColor.g}, ${saturationBgColor.b})`;

  // 计算预设颜色网格宽度
  // const presetWidth = presetColumns * 20 + (presetColumns - 1) * 6; // 20px色块 + 6px间隙

  const [tab, setTab] = useState("hex");

  // HEX 输入：受控模式下仅在凑满 6 位时才会触发 onChange，需本地草稿才能显示输入过程
  const [hexInputFocused, setHexInputFocused] = useState(false);
  const [hexDraft, setHexDraft] = useState("");

  useEffect(() => {
    if (!hexInputFocused) return;
    setHexDraft((currentColor || "").replace("#", ""));
  }, [currentColor, hexInputFocused]);

  const hexInputDisplay = hexInputFocused
    ? hexDraft
    : (currentColor || "").replace("#", "");

  return (
    <>
      <div className={`palette-el-box ${disabled ? "disabled" : ""}`}>
        {/* 上方调色板,色条,预设颜色 */}
        <div className="palette-el-saturation-hueSlider-presetColors">
          {/* 调色板 */}
          <div className="palette-el-saturation">
            <div
              ref={saturationRef}
              className="palette-sc-saturationPanel"
              style={{
                width: saturationWidth,
                height: saturationHeight,
              }}
              onMouseDown={handleSaturationMouseDown}
            >
              {/* 1. 背景遮罩层：负责显示颜色、渐变和圆角裁切 */}
              <div
                className="palette-sc-saturationMask"
                style={{ backgroundColor: saturationBg }}
              >
                <div className="palette-sc-saturationWhite">
                  <div className="palette-sc-saturationBlack" />
                </div>
              </div>

              {/* 2. 选中点：移到 Mask 外部，这样就不会被 overflow:hidden 切掉 */}
              <div
                className="palette-sc-saturationPointer"
                style={{
                  left: `${saturation}%`,
                  top: `${100 - brightness}%`,
                }}
              >
                <div className="palette-sc-saturationCircle" />
              </div>
            </div>
          </div>
          {/* 色条 */}
          <div className="palette-el-hueSlider">
            <div
              ref={hueRef}
              className="palette-sc-hueSlider"
              style={{
                width: hueWidth,
                height: hueHeight,
              }}
              onMouseDown={handleHueMouseDown}
            >
              <div
                className="palette-sc-huePointer"
                style={{ top: `${(hue / 360) * 100}%` }}
              >
                <div
                  className="palette-sc-hueCircle"
                  style={{ width: hueWidth }}
                />
              </div>
            </div>
          </div>
          {/* 预设颜色 */}
          <div className="palette-el-presetColors">
            {presetColors && presetColors.length > 0 && (
              <div
                className="palette-sc-presetColors"
                style={{
                  gridTemplateColumns: `repeat(${presetColumns}, 1fr)`,
                }}
              >
                {presetColors.map((color, index) => (
                  <button
                    key={index}
                    className="palette-sc-presetColor"
                    style={{ backgroundColor: color }}
                    onClick={() => handlePresetClick(color)}
                    disabled={disabled}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {/* 下方RGB,HEX,清除自定义颜色按钮 */}
        <div className="palette-el-rgb-hex-clearBtn">
          <div className="palette-el-rgb-hex-group">
            <div className="palette-el-rgb-hex-tab">
              <span
                className={`${tab === "hex" ? "palette-el-tab-active" : ""}`}
                onClick={() => setTab("hex")}
              >
                HEX
              </span>
              <span
                className={`${tab === "rgb" ? "palette-el-tab-active" : ""}`}
                onClick={() => setTab("rgb")}
              >
                RGB
              </span>
            </div>
            {tab === "hex" ? (
              <div className="palette-el-hex-group">
                <div className="palette-sc-hexGroup">
                  <div className="palette-sc-hexInputWrapper">
                    <span className="palette-sc-hexPrefix">#</span>
                    <input
                      type="text"
                      value={hexInputDisplay}
                      onChange={(e) => handleHexChange(e.target.value)}
                      onFocus={() => {
                        setHexInputFocused(true);
                        setHexDraft((currentColor || "").replace("#", ""));
                      }}
                      onBlur={() => {
                        setHexInputFocused(false);
                        setHexDraft("");
                      }}
                      maxLength={6}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="palette-el-rgb-group">
                <div className="palette-sc-rgbGroup">
                  <div className="palette-sc-rgbInputs">
                    <div className="palette-sc-colorInput">
                      <label className="palette-sc-colorLabel">R</label>
                      <input
                        type="number"
                        value={rgb.r}
                        onChange={(e) => handleRgbChange("r", e.target.value)}
                        min="0"
                        max="255"
                        disabled={disabled}
                      />
                    </div>
                    <div className="palette-sc-colorInput">
                      <label className="palette-sc-colorLabel">G</label>
                      <input
                        type="number"
                        value={rgb.g}
                        onChange={(e) => handleRgbChange("g", e.target.value)}
                        min="0"
                        max="255"
                        disabled={disabled}
                      />
                    </div>
                    <div className="palette-sc-colorInput">
                      <label className="palette-sc-colorLabel">B</label>
                      <input
                        type="number"
                        value={rgb.b}
                        onChange={(e) => handleRgbChange("b", e.target.value)}
                        min="0"
                        max="255"
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {showClearButton && (
            <div className="palette-el-clear-btn">
              <span
                className={disabled ? "palette-el-clear-btn-disabled" : ""}
                onClick={disabled ? undefined : onClearColorCustom}
                aria-disabled={disabled}
              >
                {t("keyboard.lighting.clearKeyColor")}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
